<template>
  <div>
    <confirmation-dialog
      v-model="showInactiveConfirmation"
      @confirm="verifySpouseUser(true)"
    >
      Are you sure you want to deactivate this
      {{ clientMarkedInactive ? 'client' : 'entity' }}? All questionnaires from
      this {{ clientMarkedInactive ? 'client’s entities' : 'entity' }} will be
      locked, and the template schedulers removed.
    </confirmation-dialog>

    <confirmation-dialog
      v-model="showSpouseUserConfirmation"
      @confirm="saveEntity(false, existingUser)"
      type="info"
    >
      <template v-if="existingUser">
        User
        <b
          >{{ existingUser.fname }}, {{ existingUser.lname }} ({{
            existingUser.email
          }})</b
        >
        already exists in the system, do you want to assign it as the spouse?
      </template>
    </confirmation-dialog>

    <v-dialog
      v-model="openDialog"
      width="800"
      persistent
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="openDialog = false"
    >
      <v-card block flat v-if="selectedEntity">
        <sc-flex :is-y-centered="true" :is-between="true" :padding="16">
          <sc-h4>Edit Entity Information</sc-h4>
          <!-- <sc-label
            v-if="!selectedUser.preferred_contact_method"
            type="warning"
            icon="warning-circle"
            is-small="true"
            >Unsubscribed</sc-label
          > -->
        </sc-flex>

        <v-card-text v-if="isIndividualEntity" class="pt-0 mb-0">
          <v-alert
            v-if="
              get(selectedEntity, 'user.preferences.has_temp_email', false) &&
                (!hasSpouse || !spouseInfo.needsAccess)
            "
            class="text-xs-left"
            type="info"
            :value="true"
            outline
          >
            User without contact email.
            <a href="#" @click.prevent.stop="focusEmailInput()">Add an email</a>
          </v-alert>

          <v-tabs v-model="activeTab" color="white" slider-color="primary">
            <v-tab key="general" ripple class="text-none subheading">
              General
            </v-tab>
            <v-tab key="contact" ripple class="text-none subheading">
              Contact
            </v-tab>
            <v-tab key="spouse" ripple class="text-none subheading">
              Spouse
            </v-tab>
            <v-tab key="setting" ripple class="text-none subheading">
              Setting
            </v-tab>

            <v-tab-item key="general">
              <v-layout wrap class="text-xs-left pt-4">
                <v-flex xs4 class="px-2">
                  <div>
                    First Name
                    <span class="error--text ml-1">*</span>
                  </div>
                  <v-text-field
                    name="first-name"
                    placeholder="John"
                    type="text"
                    v-model="selectedUser.fname"
                    required
                    :rules="rules.presence"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>
                    Last Name
                    <span class="error--text ml-1">*</span>
                  </div>
                  <v-text-field
                    name="last-name"
                    placeholder="Smith"
                    type="text"
                    v-model="selectedUser.lname"
                    required
                    :rules="rules.presence"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>Preferred Name</div>
                  <v-text-field
                    name="preferred-name"
                    placeholder="Johnny"
                    type="text"
                    v-model="selectedUser.preferred_name"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>
                    Entity Type
                    <span class="error--text ml-1">*</span>
                  </div>
                  <v-combobox
                    v-model="selectedEntity.entity_type"
                    :items="
                      otherUserCompanies.find(
                        c => c.entity_type === 'Individual',
                      )
                        ? newTypesWithoutIndividual
                        : newTypes
                    "
                    solo
                    class="default-input"
                    disabled
                  />
                </v-flex>
                <v-flex xs4 class="px-2" v-if="externalEntityIdAllowed">
                  <div>External Entity ID</div>
                  <v-text-field
                    placeholder="Optional"
                    v-model="selectedEntity.external_entity_id"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2" v-if="externalUserIdAllowed">
                  <div>External User ID</div>
                  <v-text-field
                    placeholder="Optional"
                    v-model="selectedUser.external_user_id"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2" v-if="sorabanConnectEnabled">
                  <div>
                    Tax Software Return ID
                    <v-tooltip top>
                      <v-icon size="16" slot="activator">info</v-icon>
                      <span
                        >Changing the return ID will affect data entry.</span
                      >
                    </v-tooltip>
                  </div>
                  <v-text-field
                    placeholder="JOHNSMITH"
                    v-model="selectedEntity.tax_return_id"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex
                  xs12
                  v-if="currentUser.overview_setting.use_contact_tags && !admin"
                  class="px-2"
                >
                  <div>Client Tags</div>
                  <v-autocomplete
                    class="default-input"
                    :items="firmContactTags"
                    v-model="selectedClientTags"
                    placeholder="None"
                    item-text="name"
                    item-value="name"
                    solo
                    chips
                    small-chips
                    multiple
                  />
                </v-flex>

                <v-flex
                  xs12
                  v-if="currentUser.overview_setting.use_entity_tags && !admin"
                  class="px-2"
                >
                  <div>Entity Tags</div>
                  <v-autocomplete
                    class="default-input"
                    :items="firmEntityTags"
                    v-model="selectedEntityTags"
                    placeholder="None"
                    item-text="name"
                    item-value="name"
                    solo
                    chips
                    small-chips
                    multiple
                  />
                </v-flex>
                <template v-if="allowGroupChanges">
                  <v-flex xs12 class="px-2">
                    <v-layout class="text-xs-left"> Assigned Group </v-layout>
                    <v-select
                      type="text"
                      :items="currentFirm.firm_groups"
                      placeholder="Select group"
                      v-model="userGroupId"
                      solo
                      class="default-input"
                      item-text="name"
                      item-value="id"
                      required
                    />
                  </v-flex>
                  <v-flex v-if="showAssignUserSelect" xs12 class="px-2">
                    <v-alert
                      class="text-xs-left"
                      type="info"
                      :value="true"
                      outline
                    >
                      Assign to an admin user or a user within the new assigned
                      group.
                    </v-alert>
                    <v-layout class="text-xs-left">
                      Assigned To
                      <span class="error--text ml-1">*</span>
                    </v-layout>
                    <v-select
                      class="default-input body-1"
                      solo
                      :items="filteredFirmUsers"
                      v-model="newAssignedUserId"
                      placeholder="Select a user"
                      item-text="name"
                      item-value="id"
                    />
                  </v-flex>
                </template>
              </v-layout>
              <v-layout
                class="text-xs-left"
                v-if="isAdminOrFull && useFirmRoles"
              >
                <v-flex xs6 class="px-2">
                  <div>Preparer</div>
                  <v-select
                    class="default-input"
                    solo
                    :items="firmUsersOptionsForUseFirmRoles"
                    v-model="selectedUser.cpa_id"
                    placeholder="No Preparer Assigned"
                    item-text="name"
                    item-value="id"
                  />
                </v-flex>
                <v-flex xs6 class="px-2">
                  <div>Client Manager</div>
                  <v-select
                    class="default-input"
                    solo
                    :items="firmUsersOptionsForUseFirmRoles"
                    v-model="selectedUser.client_manager_id"
                    placeholder="No Client Manager Assigned"
                    item-text="name"
                    item-value="id"
                  />
                </v-flex>
              </v-layout>
            </v-tab-item>

            <v-tab-item key="contact">
              <v-layout wrap class="text-xs-left pt-4">
                <v-flex xs4 class="px-2">
                  <div>Email</div>
                  <v-text-field
                    type="text"
                    v-model="$v.selectedUser.email.$model"
                    :error="$v.selectedUser.email.$error"
                    :error-messages="
                      $v.selectedUser.email.$error
                        ? ['E-mail must be valid']
                        : []
                    "
                    solo
                    ref="editDialogUserEmail"
                    placeholder="User email address"
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>Work Email</div>
                  <v-text-field
                    type="text"
                    v-model="selectedEntity.business_email"
                    solo
                    placeholder="Optional"
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>Contact Method</div>
                  <v-select
                    class="default-input"
                    solo
                    item-value="value"
                    item-text="label"
                    placeholder="Leave blank to let client decide"
                    :items="preferredContactMethods"
                    v-model="selectedUser.preferred_contact_method"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>Phone number</div>
                  <v-text-field
                    name="phone-number"
                    type="phone"
                    mask="phone"
                    placeholder="(123) 456 - 7890"
                    v-model="selectedUser.phone_number"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>Work Phone number</div>
                  <v-text-field
                    name="phone-number"
                    type="phone"
                    mask="phone"
                    placeholder="Optional"
                    v-model="selectedUser.work_phone_number"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs4 class="px-2">
                  <div>Work Phone Extension</div>
                  <v-text-field
                    placeholder="Optional"
                    v-model="selectedUser.work_phone_extension"
                    solo
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs6 class="px-2">
                  <div class="text-xs-left">Time Zone</div>
                  <v-select
                    v-model="selectedUser.time_zone"
                    solo
                    :items="timeZones"
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs6 class="px-2">
                  <div>Street Address</div>
                  <v-text-field
                    solo
                    label="Street Address"
                    v-model="selectedEntity.street"
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs3 class="px-2">
                  <div>Country</div>
                  <v-autocomplete
                    solo
                    label="Country"
                    clearable
                    :items="otherCountries"
                    v-model="selectedEntity.country"
                    @change="handleChangeCountry"
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs3 class="px-2">
                  <div>City</div>
                  <v-text-field
                    solo
                    label="City"
                    v-model="selectedEntity.city"
                    class="default-input"
                  />
                </v-flex>
                <v-flex xs3 class="px-2">
                  <div>
                    {{
                      isEmpty(selectedEntity.country)
                        ? 'State'
                        : 'Province/State'
                    }}
                  </div>
                  <v-text-field
                    v-if="selectedEntity.country"
                    solo
                    label="State"
                    v-model="selectedEntity.state"
                    class="default-input"
                    placeholder="Province or State"
                  />
                  <v-autocomplete
                    v-else
                    solo
                    hide-selected
                    label="State"
                    v-model="selectedEntity.state"
                    class="default-input"
                    :items="states"
                  />
                </v-flex>
                <v-flex xs3 class="px-2">
                  <div>
                    {{
                      isEmpty(selectedEntity.country)
                        ? 'Zip Code'
                        : 'Postal Code'
                    }}
                  </div>
                  <v-text-field
                    solo
                    label="Zip"
                    v-model="selectedEntity.zip"
                    class="default-input"
                  />
                </v-flex>
              </v-layout>
            </v-tab-item>

            <v-tab-item key="spouse">
              <v-layout wrap class="text-xs-left">
                <v-alert
                  v-if="
                    get(
                      selectedEntity,
                      'user.preferences.has_temp_email',
                      false,
                    ) &&
                      hasSpouse &&
                      spouseInfo.needsAccess
                  "
                  class="text-xs-left mb-0 mt-2 mx-0"
                  type="info"
                  :value="true"
                  outline
                >
                  Spouse will be made primary contact since the taxpayer doesn't
                  have access.
                  <a href="#" @click.prevent.stop="focusEmailInput()"
                    >Add email to taxpayer instead</a
                  >
                </v-alert>

                <v-switch
                  class="pl-2 default-input mt-0"
                  v-if="isIndividual"
                  v-model="hasSpouse"
                  label="Has Spouse?"
                  hide-details
                />

                <v-layout
                  wrap
                  class="text-xs-left"
                  v-if="hasSpouse && isIndividual"
                >
                  <v-flex xs6 class="px-2">
                    <div>
                      First Name
                      <span class="error--text ml-1">*</span>
                    </div>
                    <v-text-field
                      solo
                      label="First Name"
                      v-model="spouseInfo.fname"
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs6 class="px-2">
                    <div>Last Name</div>
                    <v-text-field
                      solo
                      label="Last Name"
                      v-model="spouseInfo.lname"
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs6 class="px-2">
                    <div>Email</div>
                    <v-text-field
                      solo
                      placeholder="Spouse email"
                      v-model="$v.spouseInfo.email.$model"
                      :error="$v.spouseInfo.email.$error"
                      :error-messages="
                        $v.spouseInfo.email.$error
                          ? ['E-mail must be valid']
                          : []
                      "
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs6 class="px-2">
                    <div>Work Email</div>
                    <v-text-field
                      solo
                      placeholder="Optional"
                      v-model="$v.spouseInfo.work_email.$model"
                      :error="$v.spouseInfo.work_email.$error"
                      :error-messages="
                        $v.spouseInfo.work_email.$error
                          ? ['E-mail must be valid']
                          : []
                      "
                      class="default-input"
                    />
                  </v-flex>
                  <v-flex xs6 class="px-2">
                    <div>Phone Number</div>
                    <v-text-field
                      solo
                      placeholder="Optional"
                      v-model="spouseInfo.phone_number"
                      class="default-input"
                      mask="phone"
                    />
                  </v-flex>
                  <v-flex xs3 class="pl-2">
                    <div>
                      Is Taxpayer
                      <v-tooltip top>
                        <v-icon size="16" slot="activator">info</v-icon>
                        <span>Select if the spouse is the taxpayer</span>
                      </v-tooltip>
                    </div>
                    <v-switch
                      class="default-input ma-0 pa-0"
                      solo
                      :label="spouseInfo.is_taxpayer ? 'Yes' : 'No'"
                      v-model="spouseInfo.is_taxpayer"
                    />
                  </v-flex>
                  <v-flex xs3 class="pr-2">
                    <div>
                      Needs dashboard access
                      <v-tooltip top>
                        <v-icon size="16" slot="activator">info</v-icon>
                        <span
                          >Needs spouse email and email must be different than
                          primary contact email</span
                        >
                      </v-tooltip>
                    </div>
                    <v-switch
                      class="default-input ma-0 pa-0"
                      :disabled="!enableDashboardAccess"
                      solo
                      :label="spouseInfo.needsAccess ? 'Yes' : 'No'"
                      v-model="spouseInfo.needsAccess"
                    />
                  </v-flex>
                </v-layout>
              </v-layout>
            </v-tab-item>

            <v-tab-item key="setting">
              <v-layout wrap class="text-xs-left pb-4">
                <v-flex xs12 class="px-2">
                  <div>Entity Settings</div>

                  <v-layout class="mt-2">
                    <v-switch
                      slot="activator"
                      :input-value="selectedEntity.inactive"
                      @change="selectedEntity.inactive = $event"
                      hide-details
                      class="default-input my-0 mr-1"
                    />
                    <v-layout class="pl-2" column justify-center>
                      <div class="option-title">Deactivate this entity</div>
                      <div class="option-description">
                        The client will not be able to access this entity.
                      </div>
                    </v-layout>
                  </v-layout>

                  <v-layout class="mt-2">
                    <v-switch
                      slot="activator"
                      v-model="selectedEntity.readonly_access"
                      hide-details
                      class="default-input my-0 mr-1"
                      :disabled="selectedEntity.inactive"
                    />
                    <v-layout class="pl-2" column justify-center>
                      <div class="option-title">Make this entity read-only</div>
                      <div class="option-description">
                        The client can still access existing documents, but they
                        will not be able to upload new documents or complete
                        questionnaires for this entity.
                      </div>
                    </v-layout>
                  </v-layout>
                </v-flex>

                <v-flex xs12 class="px-2 pt-4">
                  <div>Account Settings</div>

                  <v-layout class="mt-2">
                    <v-switch
                      slot="activator"
                      :input-value="!selectedUser.paying"
                      @change="selectedUser.paying = !$event"
                      hide-details
                      class="default-input my-0 mr-1"
                    />
                    <v-layout class="pl-2" column justify-center>
                      <div class="option-title">Deactivate account</div>
                      <div class="option-description">
                        The client will not be able to log in or receive
                        notifications. The client will no longer have access to
                        any entities.
                      </div>
                    </v-layout>
                  </v-layout>

                  <v-layout class="mt-2">
                    <v-switch
                      slot="activator"
                      v-model="selectedUser.readonly_access"
                      hide-details
                      class="default-input my-0 mr-1"
                      :disabled="!selectedUser.paying"
                    />
                    <v-layout class="pl-2" column justify-center>
                      <div class="option-title">Make account read-only</div>
                      <div class="option-description">
                        The client can still log in to access existing
                        documents, but they will not be able to upload new
                        documents, complete questionnaires or send messages.
                      </div>
                    </v-layout>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-tab-item>
          </v-tabs>
        </v-card-text>

        <v-card-text v-else>
          <v-layout wrap class="text-xs-left pb-4">
            <v-flex xs4 class="px-2">
              <div>
                Entity Type
                <span class="error--text ml-1">*</span>
              </div>
              <v-combobox
                v-model="selectedEntity.entity_type"
                :items="
                  otherUserCompanies.find(c => c.entity_type === 'Individual')
                    ? newTypesWithoutIndividual
                    : newTypes
                "
                solo
                :disabled="isIndividual"
                class="default-input"
              />
            </v-flex>

            <v-flex xs4 class="px-2">
              <div>
                Entity Name
                <span class="error--text ml-1">*</span>
              </div>
              <v-text-field
                name="company-name"
                placeholder="e.g. VentureScalers"
                type="text"
                v-model="selectedEntity.name"
                required
                :rules="rules.presence"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2">
              <div>Business/Work Email</div>
              <v-text-field
                placeholder="Optional"
                v-model="selectedEntity.business_email"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2" v-if="!isIndividual">
              <div>Other Email</div>
              <v-text-field
                placeholder="Optional"
                v-model="selectedEntity.other_email"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2" v-if="externalEntityIdAllowed">
              <div>External Entity ID</div>
              <v-text-field
                placeholder="Optional"
                v-model="selectedEntity.external_entity_id"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2" v-if="externalUserIdAllowed">
              <div>External User ID</div>
              <v-text-field
                placeholder="Optional"
                v-model="selectedUser.external_user_id"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2" v-if="sorabanConnectEnabled">
              <div>
                Tax Software Return ID
                <v-tooltip top>
                  <v-icon size="16" slot="activator">info</v-icon>
                  <span>Changing the return ID will affect data entry.</span>
                </v-tooltip>
              </div>
              <v-text-field
                placeholder="JOHNSMITH"
                v-model="selectedEntity.tax_return_id"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs8 :xs12="currentFirm && !isIndividual" class="px-2">
              <div>Street Address</div>
              <v-text-field
                solo
                label="Street Address"
                v-model="selectedEntity.street"
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2">
              <div>Country</div>
              <v-autocomplete
                solo
                label="Country"
                :items="otherCountries"
                v-model="selectedEntity.country"
                @change="handleChangeCountry"
                class="default-input"
              />
            </v-flex>

            <v-layout class="mb-4">
              <v-flex xs4 class="px-2">
                <div>City</div>
                <v-text-field
                  solo
                  hide-details
                  label="City"
                  v-model="selectedEntity.city"
                  class="default-input"
                />
              </v-flex>
              <v-flex xs4 class="px-2">
                <div>
                  {{
                    isEmpty(selectedEntity.country) ? 'State' : 'Province/State'
                  }}
                </div>
                <v-autocomplete
                  solo
                  hide-selected
                  hide-details
                  label="State"
                  v-model="selectedEntity.state"
                  class="default-input"
                  :items="states"
                />
              </v-flex>
              <v-flex xs4 class="px-2">
                <div>
                  {{
                    isEmpty(selectedEntity.country) ? 'Zip Code' : 'Postal Code'
                  }}
                </div>
                <v-text-field
                  solo
                  hide-details
                  label="Zip"
                  v-model="selectedEntity.zip"
                  class="default-input"
                  mask="#####"
                />
              </v-flex>
            </v-layout>

            <v-flex
              xs12
              v-if="currentUser.overview_setting.use_entity_tags && !admin"
              class="mt-1 px-2"
            >
              <div>Entity Tags</div>
              <v-autocomplete
                class="default-input"
                :items="firmEntityTags"
                v-model="selectedEntityTags"
                placeholder="None"
                item-text="name"
                item-value="name"
                solo
                chips
                hide-details
                small-chips
                multiple
              />
            </v-flex>
          </v-layout>

          <!-- FIXME: when do we use the following v-layout? -->
          <v-layout wrap class="text-xs-left">
            <v-flex v-if="isIndividual" class="pl-2">
              <div>Spouse Settings</div>

              <v-switch
                class="pl-0 mt-0 default-input"
                v-model="hasSpouse"
                label="Has Spouse?"
              />
            </v-flex>

            <v-layout
              wrap
              class="text-xs-left"
              v-if="hasSpouse && isIndividual"
            >
              <v-flex xs6 class="px-2">
                <div>
                  Spouse First Name
                  <span class="error--text ml-1">*</span>
                </div>
                <v-text-field
                  solo
                  label="First Name"
                  v-model="spouseInfo.fname"
                  class="default-input"
                />
              </v-flex>
              <v-flex xs6 class="px-2">
                <div>Spouse Last Name</div>
                <v-text-field
                  solo
                  label="Last Name"
                  v-model="spouseInfo.lname"
                  class="default-input"
                />
              </v-flex>
              <v-flex xs6 class="px-2">
                <div>Spouse Email</div>
                <v-text-field
                  solo
                  placeholder="Spouse email address"
                  v-model="$v.spouseInfo.email.$model"
                  :error="$v.spouseInfo.email.$error"
                  :error-messages="
                    $v.spouseInfo.email.$error ? ['E-mail must be valid'] : []
                  "
                  class="default-input"
                />
              </v-flex>
              <v-flex xs6 class="px-2">
                <div>Spouse Work Email</div>
                <v-text-field
                  solo
                  placeholder="Optional"
                  v-model="$v.spouseInfo.work_email.$model"
                  :error="$v.spouseInfo.work_email.$error"
                  :error-messages="
                    $v.spouseInfo.work_email.$error
                      ? ['E-mail must be valid']
                      : []
                  "
                  class="default-input"
                />
              </v-flex>
              <v-flex xs6 class="px-2">
                <div>Spouse Phone Number</div>
                <v-text-field
                  solo
                  placeholder="Optional"
                  v-model="spouseInfo.phone_number"
                  class="default-input"
                  mask="phone"
                />
              </v-flex>
              <v-flex xs3 class="pl-2">
                <div>
                  Is Taxpayer
                  <v-tooltip top>
                    <v-icon size="16" slot="activator">info</v-icon>
                    <span>Select if the spouse is the taxpayer</span>
                  </v-tooltip>
                </div>
                <v-switch
                  class="default-input ma-0 pa-0"
                  solo
                  :label="spouseInfo.is_taxpayer ? 'Yes' : 'No'"
                  v-model="spouseInfo.is_taxpayer"
                />
              </v-flex>
              <v-flex xs3 class="pr-2">
                <div>
                  Needs dashboard access
                  <v-tooltip top>
                    <v-icon size="16" slot="activator">info</v-icon>
                    <span>Needs spouse email</span>
                  </v-tooltip>
                </div>
                <v-switch
                  class="default-input ma-0 pa-0"
                  :disabled="!spouseInfo.email || $v.spouseInfo.email.$error"
                  solo
                  :label="spouseInfo.needsAccess ? 'Yes' : 'No'"
                  v-model="spouseInfo.needsAccess"
                />
              </v-flex>
            </v-layout>
          </v-layout>

          <v-layout class="text-xs-left mb-4">
            <v-flex xs12 class="px-2">
              <div>Entity Settings</div>

              <v-layout class="mt-2">
                <v-switch
                  slot="activator"
                  :input-value="selectedEntity.inactive"
                  @change="selectedEntity.inactive = $event"
                  hide-details
                  class="default-input my-0 mr-1"
                />
                <v-layout class="pl-2" column justify-center>
                  <div class="option-title">Deactivate this entity</div>
                  <div class="option-description">
                    The client will not be able to access this entity.
                  </div>
                </v-layout>
              </v-layout>

              <v-layout class="mt-2">
                <v-switch
                  slot="activator"
                  v-model="selectedEntity.readonly_access"
                  hide-details
                  class="default-input my-0 mr-1"
                  :disabled="selectedEntity.inactive"
                />
                <v-layout class="pl-2" column justify-center>
                  <div class="option-title">Make this entity read-only</div>
                  <div class="option-description">
                    The client can still access existing documents, but they
                    will not be able to upload new documents or complete
                    questionnaires for this entity.
                  </div>
                </v-layout>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            class="primary text-none"
            :disabled="!entitySavable || !areGroupChangesValid"
            @click="
              clientMarkedInactive || entityMarkedInactive
                ? requestConfirmation()
                : verifySpouseUser(false)
            "
          >
            Save
          </v-btn>
          <v-btn class="error--text text-none" flat @click="openDialog = false">
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import API from '@/api';
import { mapGetters, mapState } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import {
  otherCountries,
  preferredContactMethods,
  rules,
  states,
  timeZones,
  emailFormatter,
} from '@/constants/variables';
import { email, required } from 'vuelidate/lib/validators';
import ConfirmationDialog from '../ConfirmationDialog';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  name: 'EditEntityDialog',
  components: { ConfirmationDialog },
  mixins: [FirmPreferenceMixins],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    admin: {
      type: Boolean,
      default: false,
    },
    firmUsers: {
      type: Array,
    },
    firmContactTags: {
      type: Array,
    },
    firmEntityTags: {
      type: Array,
    },
    newTypes: {
      type: Array,
    },
    selectedEntity: {
      type: Object,
    },
    selectedUser: {
      type: Object,
    },
    isIndividualEntity: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    value(newVal) {
      this.openDialog = newVal;
      if (newVal) {
        this.initForm();
      } else {
        this.activeTab = 0;
      }
    },
    showAssignUserSelect(newVal, prevVal) {
      if (!newVal && prevVal) {
        this.newAssignedUserId = null;
      }
    },
    openDialog(newVal) {
      this.$emit('input', newVal);
    },
  },
  validations() {
    let validations = {
      spouseInfo: {
        email: {
          email: val => email(emailFormatter(val)),
        },
        work_email: {
          email: val => email(emailFormatter(val)),
        },
      },
      selectedUser: {
        email: {
          email: val => email(emailFormatter(val)),
        },
      },
    };

    // Make required if already has an email
    if (!get(this.spouse, 'preferences.has_temp_email', false)) {
      validations.spouseInfo.email = {
        required,
        email: val => email(emailFormatter(val)),
      };
    }

    return validations;
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
      defaultCompany: 'defaultCompany',
    }),
    firmUsersOptionsForUseFirmRoles() {
      return [{ id: null, name: 'None' }, ...this.firmUsers];
    },
    isAdminOrFull() {
      return (
        this.currentUser.firm_access === 'partner' ||
        this.currentUser.firm_access === 'full'
      );
    },
    enableDashboardAccess() {
      return (
        this.spouseInfo.email &&
        !this.$v.spouseInfo.email.$error &&
        this.spouseInfo.email !== this.selectedUser.email
      );
    },
    filteredFirmUsers() {
      return this.allowGroupChanges
        ? this.firmUsers.filter(
            user =>
              user.access === 'partner' ||
              (user.group && user.group.id === this.userGroupId),
          )
        : [];
    },
    allowGroupChanges() {
      return this.currentUser.firm_access === 'partner' && this.hasFirmGroups;
    },
    showAssignUserSelect() {
      return (
        this.userGroupId !==
        get(this.selectedEntity, 'user.firm_group.id', null)
      );
    },
    entitySavable() {
      return (
        (this.selectedEntity.entity_type === 'Individual' &&
          ((!this.$v.selectedUser.email.$error &&
            get(
              this.selectedEntity,
              'user.preferences.has_temp_email',
              false,
            ) &&
            !this.selectedUser.email) ||
            (this.selectedUser.email && !this.$v.selectedUser.email.$error)) &&
          this.spouseSavable) ||
        (this.selectedEntity.entity_type !== 'Individual' &&
          this.selectedEntity.name)
      );
    },
    areGroupChangesValid() {
      return (
        !this.allowGroupChanges ||
        !this.showAssignUserSelect ||
        (this.allowGroupChanges &&
          this.showAssignUserSelect &&
          !!this.newAssignedUserId)
      );
    },
    spouseSavable() {
      return (
        !this.hasSpouse ||
        (!isEmpty(this.spouseInfo.fname) && !this.$v.spouseInfo.email.$error)
      );
    },
    isIndividual() {
      return (
        this.selectedEntity && this.selectedEntity.entity_type === 'Individual'
      );
    },
    newTypesWithoutIndividual() {
      return this.newTypes.filter(type => type !== 'Individual');
    },
    otherUserCompanies() {
      return this.companies.filter(c => {
        return (
          c.user.id === this.selectedUser.id && c.id !== this.selectedEntity.id
        );
      });
    },
    clientMarkedInactive() {
      return (
        this.selectedClient && !this.selectedClient.paying && this.userWasActive
      );
    },
    entityMarkedInactive() {
      return (
        this.selectedEntity &&
        this.selectedEntity.inactive &&
        this.entityWasActive
      );
    },
  },
  methods: {
    get,
    isEmpty,
    focusEmailInput() {
      this.activeTab = 1;
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.editDialogUserEmail.$refs.input.focus();
        }, 500);
      });
    },
    requestConfirmation() {
      this.showInactiveConfirmation = true;
    },
    handleChangeCountry(value) {
      if (!value) {
        this.selectedEntity.country = null;
      }
    },
    async handleSpouse(spouseUser) {
      if (spouseUser) {
        await API.company.createSpouseAssigment({
          companyId: this.selectedEntity.id,
          userId: this.existingUser.id,
        });
        this.showSpouseUserConfirmation = false;
      } else {
        if (this.hasSpouse && this.spouse && this.spouse.id) {
          await API.company.updateSpouse({
            companyId: this.selectedEntity.id,
            id: this.spouse.id,
            data: {
              fname: this.spouseInfo.fname,
              lname: this.spouseInfo.lname,
              phone_number: this.spouseInfo.phone_number,
              email: this.spouseInfo.email,
              work_email: this.spouseInfo.work_email,
              is_taxpayer: this.spouseInfo.is_taxpayer,
              needsAccess: this.spouseInfo.needsAccess,
            },
          });
        } else if (this.hasSpouse) {
          await API.company.createSpouse({
            companyId: this.selectedEntity.id,
            fname: this.spouseInfo.fname,
            lname: this.spouseInfo.lname,
            phone_number: this.spouseInfo.phone_number,
            email: this.spouseInfo.email,
            work_email: this.spouseInfo.work_email,
            is_taxpayer: this.spouseInfo.is_taxpayer,
            needsAccess: this.spouseInfo.needsAccess,
          });
        } else if (this.spouse && this.spouse.id) {
          await API.company.deleteSpouse({
            companyId: this.selectedEntity.id,
            id: this.spouse.id,
          });
        }
      }
    },
    async verifySpouseUser(reloadQuestionnaires) {
      if (
        this.hasSpouse &&
        this.spouseInfo.email &&
        this.spouseInfo.email !== this.selectedUser.email && // Allows same email as primary contact
        (!this.spouse || this.spouse.email !== this.spouseInfo.email)
      ) {
        // Verify if user exists
        const userResponse = await API.user.findUserByEmail(
          this.spouseInfo.email,
        );
        if (userResponse.data) {
          this.existingUser = userResponse.data;
          this.showSpouseUserConfirmation = true;
        } else {
          await this.saveEntity(reloadQuestionnaires, null);
        }
      } else {
        await this.saveEntity(reloadQuestionnaires, null);
      }
    },
    async saveEntity(reloadQuestionnaires, spouseUser) {
      this.showInactiveConfirmation = false;
      if (
        this.selectedEntity.entity_type === 'Individual' &&
        this.isIndividualEntity
      ) {
        this.selectedEntity.name =
          this.selectedUser.lname + ', ' + this.selectedUser.fname;
      }
      if (
        this.selectedUser &&
        this.selectedUser.preferred_sign_in_method == 'sms' &&
        this.selectedUser.phone_number == ''
      ) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'The client has set their login in method through SMS verification. If you would like to disable SMS reminder for this client, please change their contact method.',
        });
        return;
      }

      this.openDialog = false;
      const {
        name,
        entity_type,
        street,
        city,
        state,
        zip,
        country,
        business_email,
        other_email,
        external_entity_id,
        inactive,
        readonly_access,
        tax_return_id,
      } = this.selectedEntity;
      const {
        fname,
        lname,
        email,
        preferred_name,
        preferred_contact_method,
        phone_number,
        paying,
        time_zone,
        work_phone_number,
        work_phone_extension,
        cpa_id,
        client_manager_id,
        external_user_id,
      } = this.selectedUser;
      try {
        const response = await API.company.updateCompany({
          name,
          fname,
          lname,
          email,
          preferred_name,
          phone_number,
          work_phone_number,
          work_phone_extension,
          entity_type,
          street,
          city,
          state,
          zip,
          inactive,
          readonly_access,
          paying,
          country,
          time_zone,
          preferred_contact_method,
          business_email,
          other_email,
          cpa_id,
          client_manager_id,
          external_entity_id,
          external_user_id,
          tax_return_id,
          id: this.selectedEntity.id,
          userId: this.selectedEntity.user.id,
          group_id: this.userGroupId,
        });

        if (
          this.allowGroupChanges &&
          this.showAssignUserSelect &&
          this.newAssignedUserId
        ) {
          await API.advisor.updateAssignments({
            ids: [this.selectedEntity.user.id],
            advisorId: this.newAssignedUserId,
          });
        }

        await this.handleSpouse(spouseUser);

        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          await this.updateClientTags(this.selectedUser);
          await this.updateEntityTags();

          this.$store.dispatch('application/setSuccessNotification', {
            message: `Information has been saved successfully`,
          });
          await this.$store.dispatch('companies/getCompanies');
          if (
            this.returnSyncEnabled &&
            this.selectedEntity.latest_update_export_task
          ) {
            this.$emit('open-review-return-detail-sync-dialog');
          }
          this.$emit('refresh');
          if (reloadQuestionnaires) {
            this.$emit('refresh-questionnaires');
          }
        }
      } catch (e) {
        console.log(e);
        if (get(e, 'response.data.message', '').indexOf('EMAIL_EXISTS') > -1) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Email is already taken.',
          });
          return;
        }

        if (get(e, 'response.data.error', false)) {
          if (e.response.data.error.message === 'EMAIL_EXISTS') {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'Email is already taken.',
            });
          } else if (e.response.data.error.message === 'INVALID_EMAIL') {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'Email is invalid.',
            });
          } else {
            if (get(e, 'response.data.error.message', false)) {
              this.$store.dispatch('application/setErrorNotification', {
                message: e.response.data.error.message,
              });
            } else {
              this.$store.dispatch('application/setErrorNotification', {
                message: e.response.data.error,
              });
            }
          }
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: e,
          });
        }
      }
    },

    async updateClientTags(user) {
      try {
        const response = await API.user.updateTags({
          userId: user.id,
          contactTags: this.selectedClientTags,
        });

        await this.$store.dispatch('companies/getCompanies');
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving tags.',
        });
      }
    },

    async updateEntityTags() {
      try {
        const response = await API.company.updateTags({
          companyId: this.selectedEntity.id,
          entityTags: this.selectedEntityTags,
        });

        await this.$store.dispatch('companies/getCompanies');
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving tags.',
        });
      }
    },

    async loadSpouse(companyId) {
      const spouseInfo = await API.company.getSpouse(companyId);
      this.spouse = spouseInfo.data;

      if (this.spouse) {
        this.hasSpouse = true;
        this.spouseInfo = {
          fname: this.spouse.fname,
          lname: this.spouse.lname,
          needsAccess: this.spouse.needsAccess,
          date_of_birth: this.spouse.dob,
          work_email: this.spouse.work_email,
          email: get(this.spouse, 'preferences.has_temp_email', false)
            ? null
            : this.spouse.email,
          phone_number: this.spouse.phone_number,
          is_taxpayer: this.spouse.is_taxpayer,
        };
      } else {
        this.hasSpouse = false;
        this.spouseInfo = {
          fname: null,
          lname: null,
          date_of_birth: null,
          needsAccess: false,
          work_email: null,
          email: null,
          phone_number: null,
          is_taxpayer: false,
        };
      }
    },
    initForm() {
      if (this.selectedEntity) {
        if (
          get(this.selectedEntity, 'user.preferences.has_temp_email', false)
        ) {
          this.selectedUser.email = null;
        }

        this.userWasActive = this.selectedUser.paying;
        this.entityWasActive = !this.selectedEntity.inactive;

        if (this.selectedEntity.entity_type === 'Individual') {
          this.loadSpouse(this.selectedEntity.id);
        }

        this.selectedClientTags = this.selectedUser.contact_tags.map(
          c => c.name,
        );
        this.selectedEntityTags = this.selectedEntity.entity_tags.map(
          c => c.name,
        );
        this.userGroupId = get(this.selectedUser, 'firm_group.id', null);
        this.newAssignedUserId = null;
        this.userGroupId = get(this.selectedEntity, 'user.firm_group.id', null);
        this.newAssignedUserId = null;
        this.existingUser = null;
        this.showSpouseUserConfirmation = false;
      }
    },
  },
  data() {
    return {
      rules,
      states,
      timeZones,
      otherCountries,
      preferredContactMethods,
      openDialog: false,
      userWasActive: false,
      entityWasActive: false,
      showSpouseUserConfirmation: false,
      existingUser: null,
      activeTab: 0,
      userGroupId: null,
      newAssignedUserId: null,
      showInactiveConfirmation: false,
      spouseInfo: {
        fname: null,
        lname: null,
        date_of_birth: null,
        email: '',
        phone_number: '',
      },
      spouse: null,
      selectedClientTags: [],
      selectedEntityTags: [],
      hasSpouse: false,
    };
  },
  created() {
    this.openDialog = this.value;
    if (this.value) {
      this.initForm();
    }
  },
};
</script>

<style scoped lang="postcss">
.option-title {
  font-size: 14px;
}

.option-description {
  font-size: 12px;
  color: #545454;
}
</style>
